import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiClockAlertOutline, mdiClose } from "@mdi/js";
import { Button, NumberField } from "@heroui/react";
import { feedbackSettingsStore } from "@/stores/FeedbackSettingsStore";
import StringListEditor from "./StringListEditor";

const NotificationsTab: React.FC = observer(() => {
  const {
    isNotificationsLoading,
    isNotificationsSaving,
    notificationsForm,
    notificationsFormErrors,
    notificationsSubmitAttempted,
    isNotificationsDirty,
    notificationsUpdatedAt,
  } = feedbackSettingsStore;

  const fieldError = (field: string) =>
    notificationsSubmitAttempted
      ? notificationsFormErrors[field]
      : undefined;

  if (isNotificationsLoading) {
    return <div className="fbs-tab__loading">Загрузка...</div>;
  }

  return (
    <div className="fbs-tab" data-testid="feedback-settings-notifications-tab">
      <div className="fbs-tab__header">
        <div>
          <h2 className="fbs-tab__title">Уведомления</h2>
          <p className="fbs-tab__desc">
            Email, push и SMS при новых обращениях в обратную связь.
            {notificationsUpdatedAt && (
              <>
                {" "}
                Обновлено:{" "}
                {new Date(notificationsUpdatedAt).toLocaleString("ru-RU")}
              </>
            )}
          </p>
        </div>
        {isNotificationsDirty && (
          <div className="fbs-tab__actions">
            <Button
              variant="ghost"
              onPress={() => feedbackSettingsStore.resetNotificationsForm()}
              isDisabled={isNotificationsSaving}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onPress={() => void feedbackSettingsStore.submitNotifications()}
              isPending={isNotificationsSaving}
              data-testid="feedback-settings-notifications-save"
            >
              Сохранить
            </Button>
          </div>
        )}
      </div>

      <div className="fbs-notifications" data-testid="feedback-settings-notifications-form">
        <section
          className="fbs-notifications__section"
          data-testid="feedback-settings-notifications-email-section"
        >
          <div className="fbs-notifications__section-head">
            <h3 className="fbs-notifications__section-title">
              Email-уведомления
            </h3>
            <p className="fbs-notifications__section-desc">
              На эти адреса будут приходить письма о новых обращениях.
            </p>
          </div>
          <StringListEditor
            items={notificationsForm.notification_emails}
            onAdd={(value) =>
              feedbackSettingsStore.addNotificationEmail(value)
            }
            onRemove={(index) =>
              feedbackSettingsStore.removeNotificationEmail(index)
            }
            placeholder="email@example.com"
            addLabel="Добавить email"
            listError={fieldError("notification_emails")}
            disabled={isNotificationsSaving}
          />
        </section>

        <section className="fbs-notifications__section">
          <div className="fbs-notifications__section-head">
            <h3 className="fbs-notifications__section-title">
              Push в приложении PROSTO
            </h3>
            <p className="fbs-notifications__section-desc">
              Номера администраторов, зарегистрированных в приложении — на них
              будут отправляться push-уведомления.
            </p>
          </div>
          <StringListEditor
            items={notificationsForm.push_phones}
            onAdd={(value) => feedbackSettingsStore.addPushPhone(value)}
            onRemove={(index) => feedbackSettingsStore.removePushPhone(index)}
            placeholder="+79001234567"
            addLabel="Добавить номер телефона"
            inputMode="phone"
            listError={fieldError("push_phones")}
            disabled={isNotificationsSaving}
          />
        </section>

        <section className="fbs-notifications__section">
          <div className="fbs-notifications__section-head">
            <h3 className="fbs-notifications__section-title">SMS-эскалация</h3>
            <p className="fbs-notifications__section-desc">
              Если новое обращение не обработано в течение заданного времени,
              на резервные номера будет отправлено SMS.
            </p>
          </div>

          <div className="fbs-notifications__toggle">
            {notificationsForm.sms_escalation_enabled ? (
              <Button
                variant="ghost"
                size="sm"
                className="fbs-string-list__add-btn"
                isDisabled={isNotificationsSaving}
                onPress={() =>
                  feedbackSettingsStore.setNotificationsField(
                    "sms_escalation_enabled",
                    false,
                  )
                }
              >
                <Icon path={mdiClose} size={0.75} />
                Отключить SMS-эскалацию
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="fbs-string-list__add-btn"
                isDisabled={isNotificationsSaving}
                onPress={() =>
                  feedbackSettingsStore.setNotificationsField(
                    "sms_escalation_enabled",
                    true,
                  )
                }
              >
                <Icon path={mdiClockAlertOutline} size={0.75} />
                Включить SMS-эскалацию
              </Button>
            )}
          </div>

          {notificationsForm.sms_escalation_enabled && (
            <>
              <div className="fbs-form-field">
                <label className="fbs-form-field__label">
                  Задержка перед отправкой SMS (минуты)
                </label>
                <NumberField
                  value={notificationsForm.sms_escalation_delay_minutes}
                  onChange={(value) => {
                    if (value == null || Number.isNaN(value)) return;
                    feedbackSettingsStore.setNotificationsField(
                      "sms_escalation_delay_minutes",
                      value,
                    );
                  }}
                  minValue={1}
                  maxValue={10080}
                  isDisabled={isNotificationsSaving}
                >
                  <NumberField.Group className="fbs-notifications__delay-field">
                    <NumberField.Input placeholder="30" />
                  </NumberField.Group>
                </NumberField>
                {fieldError("sms_escalation_delay_minutes") && (
                  <span className="fbs-form-field__error">
                    {fieldError("sms_escalation_delay_minutes")}
                  </span>
                )}
              </div>

              <div className="fbs-form-field">
                <label className="fbs-form-field__label">
                  Резервные номера для SMS
                </label>
                <StringListEditor
                  items={notificationsForm.sms_escalation_phones}
                  onAdd={(value) =>
                    feedbackSettingsStore.addSmsEscalationPhone(value)
                  }
                  onRemove={(index) =>
                    feedbackSettingsStore.removeSmsEscalationPhone(index)
                  }
                  placeholder="+79001234567"
                  addLabel="Добавить номер телефона"
                  inputMode="phone"
                  listError={fieldError("sms_escalation_phones")}
                  disabled={isNotificationsSaving}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
});

NotificationsTab.displayName = "NotificationsTab";
export default NotificationsTab;
